import { PlanogramPegboard } from "@CommonTypes/merchflow/pog/pegboard";
import { Flex, Color } from "src/elements";
import styled from "styled-components";
import { PegboardPlacement } from "./PegboardPlacement";
import { PegboardHoles } from "./PegboardHoles";
import { PogAtom } from "../../store/types";

const PegboardElement = styled(Flex)<{
  xLeft: number;
  yBottom: number;
  zIndex: number;
  pegboardHeight: number;
  pegboardWidth: number;
  scaleY: number;
  scaleX: number;
}>`
  position: absolute;
  left: ${({ xLeft, scaleX }) => `calc(${xLeft}px * ${scaleX})`};
  width: ${({ pegboardWidth, scaleX }) => `calc(${pegboardWidth}px * ${scaleX})`};
  bottom: ${({ yBottom, scaleY }) => `calc(${yBottom}px * ${scaleY})`};
  height: ${({ pegboardHeight, scaleY }) => `calc(${pegboardHeight}px * ${scaleY})`};
  z-index: ${({ zIndex }) => zIndex};
  background-color: ${Color.gray400};
  border-radius: 2px;
  box-shadow: 0px 2.7px 2.7px 0px rgba(77, 77, 77, 0.2);
`;

const PegboardHolesOffset = styled(Flex)<{
  holesOffsetHorizontal: number;
  holesOffsetVertical: number;
  scaleX: number;
  scaleY: number;
}>`
  position: absolute;
  left: ${({ holesOffsetHorizontal, scaleX }) => `calc(${holesOffsetHorizontal}px * ${scaleX})`};
  top: ${({ holesOffsetVertical, scaleY }) => `calc(${holesOffsetVertical}px * ${scaleY})`};
  width: ${({ holesOffsetHorizontal, scaleX }) =>
    `calc(100% - 1 * ${holesOffsetHorizontal}px * ${scaleX})`};
  height: ${({ holesOffsetVertical, scaleY }) =>
    `calc(100% - 1 * ${holesOffsetVertical}px * ${scaleY})`};
`;

interface Props {
  pegboard: PlanogramPegboard;
  pegboardWidth: number;
  xLeft?: number;
  zIndex?: number;
  scaleX: number;
  scaleY: number;
  highlightedItems: string[];
  pogAtom: PogAtom;
}

export const Pegboard = ({
  pegboard,
  pegboardWidth,
  xLeft = 0,
  zIndex = pegboard.yBottom,
  scaleX,
  scaleY,
  highlightedItems,
  pogAtom,
}: Props) => {
  return (
    <PegboardElement
      xLeft={xLeft}
      yBottom={pegboard.yBottom}
      zIndex={zIndex}
      pegboardHeight={pegboard.height}
      pegboardWidth={pegboardWidth}
      scaleX={scaleX}
      scaleY={scaleY}
    >
      <PegboardHolesOffset
        holesOffsetHorizontal={pegboard.holesOffsetHorizontal}
        holesOffsetVertical={pegboard.holesOffsetVertical}
        scaleX={scaleX}
        scaleY={scaleY}
      >
        <PegboardHoles
          pegboardWidth={pegboardWidth}
          pegboardHeight={pegboard.height}
          holesOffsetHorizontal={pegboard.holesOffsetHorizontal}
          holesOffsetVertical={pegboard.holesOffsetVertical}
          holesGapHorizontal={pegboard.holesGapHorizontal}
          holesGapVertical={pegboard.holesGapVertical}
        />

        {pegboard.placements.map((pegboardPlacement, i) => (
          <PegboardPlacement
            key={i}
            pegboardPlacement={pegboardPlacement}
            holesGapHorizontal={pegboard.holesGapHorizontal}
            holesGapVertical={pegboard.holesGapVertical}
            scaleX={scaleX}
            scaleY={scaleY}
            pogAtom={pogAtom}
            isHighlighted={highlightedItems.includes(pegboardPlacement.uniqueId)}
            isScrolledToView={
              highlightedItems.length === 1 && highlightedItems.includes(pegboardPlacement.uniqueId)
            }
          />
        ))}
      </PegboardHolesOffset>
    </PegboardElement>
  );
};
